<template>
    <div class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div class="sm:mx-auto sm:w-full sm:max-w-md">
            <img
                class="mx-auto h-12 w-auto"
                src="@/assets/logo-full.png"
                alt="upyield logo"
            >
            <h2 class="mt-6 text-center text-3xl leading-9 font-extrabold text-gray-900">
                Create new account
            </h2>
        </div>

        <div
            v-loading="isLoading"
            class="mt-8 mx-auto max-w-4xl"
        >
            <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                <form
                    action="#"
                    method="POST"
                >
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                            <h3 class="mb-4 text-lg leading-6 font-medium text-gray-900">
                                Account details
                            </h3>

                            <div class="mb-4">
                                <form-field
                                    v-model="companyName"
                                    :field="{
                                        name: 'company_name',
                                        type: 'text',
                                        label: 'Company name',
                                        validate: 'required',
                                    }"
                                />
                            </div>

                            <div class="mb-4 grid grid-cols-2 gap-4">
                                <form-field
                                    v-model="firstName"
                                    :field="{
                                        name: 'first_name',
                                        type: 'text',
                                        label: 'First name',
                                        validate: 'required',
                                    }"
                                />
                                <form-field
                                    v-model="lastName"
                                    :field="{
                                        name: 'last_name',
                                        type: 'text',
                                        label: 'Last name',
                                        validate: 'required',
                                    }"
                                />
                            </div>

                            <div class="grid grid-cols-2 gap-4">
                                <div class="mb-4">
                                    <form-field
                                        v-model="email"
                                        :field="{
                                            name: 'email',
                                            type: 'email',
                                            label: 'Email address',
                                            validate: 'required|email',
                                        }"
                                    />
                                </div>

                                <div class="mb-4">
                                    <form-field
                                        v-model="messenger"
                                        :field="{
                                            name: 'messenger',
                                            type: 'text',
                                            label: 'Messenger',
                                        }"
                                    />
                                </div>
                            </div>

                            <div class="grid grid-cols-2 gap-4">
                                <div>
                                    <form-field
                                        v-model="password"
                                        :field="{
                                            name: 'password',
                                            type: 'password',
                                            label: 'Password',
                                            validate: 'required',
                                        }"
                                    />
                                </div>

                                <div>
                                    <form-field
                                        v-model="passwordConfirm"
                                        :field="{
                                            name: 'password_confirmation',
                                            type: 'password',
                                            label: 'Confirm password',
                                            validate: `required|confirmed:${password}`,
                                        }"
                                    />
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 class="mb-4 text-lg leading-6 font-medium text-gray-900">
                                Traffic information
                            </h3>

                            <div class="mb-4">
                                <div class="mt-1 rounded-md shadow-sm">
                                    <form-field
                                        v-model="partnerType"
                                        :field="{
                                            name: 'form__multiselect',
                                            type: 'multiselect',
                                            label: 'What type of Publisher are you?',
                                            multiple: false,
                                            validate: 'required',
                                            options: dicts.partner_type,
                                            key: 'id',
                                            text: 'name',
                                        }"
                                    />
                                </div>
                            </div>

                            <div class="mb-4">
                                <form-field
                                    v-model="partnerTrafficTypes"
                                    :field="{
                                        name: 'form__multiselect',
                                        type: 'multiselect',
                                        label: 'What kind of traffic do you have?',
                                        multiple: true,
                                        validate: 'required',
                                        options: dicts.partner_traffic_types,
                                        key: 'name',
                                        text: 'name',
                                    }"
                                />
                            </div>

                            <div class="mb-4">
                                <form-field
                                    v-model="partnerTrafficCategories"
                                    :field="{
                                        name: 'form__multiselect',
                                        type: 'multiselect',
                                        label: 'What sort of offers do you run?',
                                        multiple: true,
                                        validate: 'required',
                                        options: dicts.partner_traffic_categories,
                                        key: 'id',
                                        text: 'name',
                                    }"
                                />
                            </div>

                            <div>
                                <form-field
                                    v-model="partnerTrafficCountries"
                                    :field="{
                                        name: 'form__multiselect',
                                        type: 'multiselect',
                                        label: 'Which countries do you have traffic from?',
                                        multiple: true,
                                        validate: 'required',
                                        options: dicts.partner_traffic_countries,
                                        key: 'code',
                                        text: 'name',
                                    }"
                                />
                            </div>

                            <div class="flex justify-end mt-2">
                                <form-field
                                    v-model="tos"
                                    :field="tosCheckbox"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="mt-6">
                        <span class="block w-full rounded-md shadow-sm">
                            <button
                                class="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white
                                bg-primary-700 hover:bg-primary-500 focus:outline-none focus:border-primary-700 focus:shadow-outline-primary
                                active:bg-primary-700 transition duration-150 ease-in-out"
                                @click.prevent="register"
                            >
                                Register new account
                            </button>
                        </span>
                    </div>
                </form>

                <div class="mt-6">
                    <div class="relative">
                        <div class="absolute inset-0 flex items-center">
                            <div class="w-full border-t border-gray-300" />
                        </div>
                        <div class="relative flex justify-center text-sm leading-5">
                            <span class="px-2 bg-white text-gray-500">
                                or if you already have an account
                            </span>
                        </div>
                    </div>

                    <div class="mt-6 grid grid-cols-2 gap-4">
                        <div>
                            <span class="w-full inline-flex rounded-md shadow-sm">
                                <router-link
                                    :to="{ name: 'Login' }"
                                    tag="button"
                                    class="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md bg-white text-sm leading-5 font-medium text-gray-500 hover:text-gray-400 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition duration-150 ease-in-out"
                                >
                                    Login
                                </router-link>
                            </span>
                        </div>

                        <div>
                            <span class="w-full inline-flex rounded-md shadow-sm">
                                <router-link
                                    :to="{ name: 'ForgotPassword' }"
                                    tag="button"
                                    class="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md bg-white text-sm leading-5 font-medium text-gray-500 hover:text-gray-400 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition duration-150 ease-in-out"
                                >
                                    Forgot password
                                </router-link>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, onMounted, ref } from '@vue/composition-api';
import { register as authRegister } from '@/store/auth';

import router from '@/router';
import FormField from 'shared-lib/components/forms/FormField.vue';
import useValidator from '@/composables/useValidator';
import config from '@/config';
import api from '@/api';

export default defineComponent({
    name: 'Register',

    components: {
        FormField,
    },

    setup (props, { root: { $validator } }) {
        useValidator();

        const companyName = ref('');
        const firstName = ref('');
        const lastName = ref('');
        const email = ref('');
        const messenger = ref('');
        const password = ref('');
        const passwordConfirm = ref('');
        const partnerType = ref('');
        const partnerTrafficTypes = ref('');
        const partnerTrafficCategories = ref('');
        const partnerTrafficCountries = ref('');
        const tos = ref(false);
        const isLoading = ref(false);
        const dicts = ref({ partner_type: {}, partner_traffic_types: {}, partner_traffic_categories: {}, partner_traffic_countries: {} });

        const tosCheckbox = ref({
            name:       'tos',
            type:       'checkbox',
            options:   [{
                value: true,
                uncheckedValue: false,
                text:  `I agree to the <a class="link" target="_blank"
                                href="${ config.value.register ? config.value.register.termsconditions_url : '#not-defined' }"
                                >Terms and Conditions</a>`,
            }],
            validate: 'required',
        });

        function register () {
            $validator.validateAll().then((result) => {
                if (result) {
                    isLoading.value = true;
                    authRegister({
                        company_name: companyName.value,
                        firstname: firstName.value,
                        lastname: lastName.value,
                        email: email.value,
                        email_confirmation: email.value,
                        messenger: messenger.value,
                        password: password.value,
                        password_confirmation: passwordConfirm.value,
                        tos: tos.value,
                        partner_type: partnerType.value,
                        partner_traffic_types: partnerTrafficTypes.value,
                        partner_traffic_categories: partnerTrafficCategories.value,
                        partner_traffic_countries: partnerTrafficCountries.value,
                    })
                        .then(() => router.push({ name: 'RegisterCompleted', params: { email: email.value } }))
                        .catch((e) => console.log(e)) // eslint-disable-line no-console
                        .finally(() => isLoading.value = false);
                }
            });
        }

        onMounted(() => {
            api.getDicts().then(response => {
                dicts.value.partner_type = response.data.partners_types.map(tag => ({ id: tag.id, name: tag.name }));
                dicts.value.partner_traffic_types = response.data.partners_traffic_types.map(tag => ({ id: tag.id, name: tag.name }));
            });
            api.getDictCategories().then(response => {
                dicts.value.partner_traffic_categories = response.data.data.map(tag => ({ id: tag.id, name: tag.name }));
            });
            api.getDictCountries().then(response => {
                dicts.value.partner_traffic_countries = response.data.data.map(tag => ({ code: tag.code, name: tag.name }));
            });
        });

        return {
            dicts,
            register,
            companyName,
            firstName,
            lastName,
            email,
            messenger,
            passwordConfirm,
            password,
            partnerType,
            partnerTrafficTypes,
            partnerTrafficCategories,
            partnerTrafficCountries,
            tos,
            isLoading,
            tosCheckbox,
        };
    },
});
</script>

<style lang="scss">
.Login .navigation {
    display: none;
}
</style>
